import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styles from './Cart.module.scss';
import { currencyFormat } from '../../../utils/formatHelper';
import StoreFormContainer from '../../../containers/blocks/StoresFormContainer';
import BootstrapTable from 'react-bootstrap-table-next';
import { sortCaret } from '../../../utils/tableHelper';
import Quantity from '../Quantity';
import { upperFirst } from 'lodash';

function formatImages(images) {
  if (!images.length) {
    return '';
  }

  return (
    <div className={styles.images}>
      <img src={`${images[0]}`} alt="" />
    </div>
  );
}

function formatOptions(options) {
  const keys = Object.keys(options);

  return (
    <div>
      {keys.map((key) => (
        <p key={key} className={styles.option}>
          <label>{upperFirst(key)}:</label>
          {options[key]}
        </p>
      ))}
    </div>
  );
}

function formatQuantity(
  quantity,
  row,
  rowIndex,
  { quantities, handleOnChangeInputSlider, handleDelete, saveQuantityToSharedStorage },
) {
  const { id, available_inventory } = row;
  return (
    <div className={styles.input_number}>
      {quantity > available_inventory && <span className={styles.reduced_quantity}>{quantity} &rarr; </span>}
      <Quantity
        value={Math.min(quantities[id].quantity, available_inventory)}
        onQuantityChange={handleOnChangeInputSlider}
        variantId={id}
        max={available_inventory}
        onBlur={(quantityValueStr, variantId) =>
          saveQuantityToSharedStorage(quantityValueStr, variantId, available_inventory)
        }
        onPressEnter={(quantityValueStr, variantId) =>
          saveQuantityToSharedStorage(quantityValueStr, variantId, available_inventory)
        }
      />
      <span onClick={() => handleDelete(id)}>
        <i className="ic-bin" />
      </span>
    </div>
  );
}

class Cart extends PureComponent {
  getShippingCostRule = (supplierRetailer, totalSupplierCost) => {
    const shippingCostRule = supplierRetailer.replenishment_order_shipping_cost.find(
      (rule) => !rule.max || rule.max >= totalSupplierCost,
    );
    return shippingCostRule ? shippingCostRule : false;
  };

  constructor(props) {
    super(props);
    this.state = {
      quantities: variantsToMap(this.props.variants),
    };
  }

  checkMinimumProducts() {
    const { retailerSuppliers } = this.props;
    const variants = Object.values(this.state.quantities);

    if (!variants.length) {
      return {
        errors: [],
        itemsCost: 0,
        shippingCost: 0,
      };
    }

    const currency = variants[0].currency;
    const errors = [];
    const bySupplier = {};

    let itemsCost = 0;
    let shippingCost = 0;

    const supplierIdtoSupplierRetailer = {};
    retailerSuppliers.forEach((supplierRetailer) => {
      const supplierId = supplierRetailer.supplier.id;
      supplierIdtoSupplierRetailer[supplierId] = supplierRetailer;
    });

    variants.forEach((variant) => {
      const supplierId = variant.supplier_id;
      const quantity = Math.min(variant.quantity, variant.available_inventory);
      const variantCost = variant.wholesale_price * quantity;

      itemsCost += variantCost;

      if (!bySupplier[supplierId]) {
        bySupplier[supplierId] = {
          quantity: 0,
          cost: 0,
        };
      }

      bySupplier[supplierId].quantity += quantity;
      bySupplier[supplierId].cost += variantCost;
    });

    Object.keys(bySupplier).forEach((supplierId) => {
      const supplierRetailer = supplierIdtoSupplierRetailer[supplierId];
      const supplierName = supplierRetailer.supplier.name;
      const minimumOrderProducts = supplierRetailer.minimum_products_for_replenishment_order;
      const minimumOrderValue = supplierRetailer.minimum_order_value_for_replenishment_order * 100;
      const currentItemsCount = bySupplier[supplierId].quantity;
      const currentCost = bySupplier[supplierId].cost;

      bySupplier[supplierId].rule = this.getShippingCostRule(supplierRetailer, currentCost);

      if (minimumOrderProducts > currentItemsCount) {
        errors.push(`You must purchase a minimum of ${minimumOrderProducts}
                        items from ${supplierName}! (Currently: ${currentItemsCount} items)`);
      }

      if (minimumOrderValue > currentCost) {
        errors.push(`You must purchase a minimum value of ${currencyFormat(minimumOrderValue, currency)}
                        from ${supplierName}! (Currently: ${currencyFormat(currentCost, currency)})`);
      }
    });

    variants.forEach((variant) => {
      const supplierId = variant.supplier_id;
      const quantity = Math.min(variant.quantity, variant.available_inventory);
      const rule = bySupplier[supplierId].rule;

      if (rule) {
        shippingCost += rule.fixed_per_unit * quantity;
        shippingCost += Math.round((rule.percentage * quantity * variant.wholesale_price) / 100);
      }
    });

    return {
      errors: errors,
      itemsCost: itemsCost,
      shippingCost: shippingCost,
      currency,
    };
  }

  checkRequiredCreditCards() {
    const { variants, retailerSuppliers, retailerId, isAdmin } = this.props;
    const errors = {};

    variants.forEach((variant) => {
      const supplierId = variant.supplier_id;
      if (errors[supplierId]) {
        return;
      }

      const supplier = retailerSuppliers.find(({ supplier }) => supplier.id === supplierId);
      if (supplier.replenish_billing_method === 'instant' && !supplier.stripe_data) {
        errors[supplierId] = variant.supplier_name;
      }
    });

    return Object.keys(errors).map((supplierId) => {
      const supplierName = errors[supplierId];

      if (isAdmin) {
        const link = <Link to={`/retailers/${retailerId}/admin/activation/${supplierId}`}>{supplierName}</Link>;
        return <span>Please provide a valid payment method for {link}</span>;
      } else {
        return (
          <span>
            Your account requires a payment method for <strong>{supplierName}</strong> - please contact your account
            admin
          </span>
        );
      }
    });
  }

  checkHasStore() {
    const { hasStore, isAdmin, retailerId } = this.props;

    const errors = [];
    if (!hasStore) {
      if (isAdmin) {
        const link = <Link to={`/retailers/${retailerId}/admin`}>add</Link>;
        errors.push(<span>Please {link} a store for billing info</span>);
      } else {
        errors.push(<span>Your account requires at least one store - please contact your account admin</span>);
      }
    }
    return errors;
  }

  saveQuantityToSharedStorage = (value, variantId, availableInventory) => {
    const cappedQuantity = Math.min(Number.parseInt(value), availableInventory);
    this.props.setQuantityItem(cappedQuantity, variantId);
  };

  handleOnChangeInputSlider = (quantity, variantId) => {
    this.setState((prevState) => {
      const previousVariant = { ...prevState.quantities[variantId] };
      const newVariant = Object.assign(previousVariant, { quantity: Number.parseInt(quantity) });
      return {
        quantities: {
          ...prevState.quantities,
          [variantId]: newVariant,
        },
        changed: true,
      };
    });
  };

  handleDelete = (variantId) => {
    this.props.deleteOrderLine(variantId);
  };

  sendOrder = (storeId, shippingAddress, shippingMethodCode, poNumber = null) => {
    const { variants, sendOrder } = this.props;
    const orderLines = [];

    variants.forEach((variant) => {
      const quantity = Math.min(variant.quantity, variant.available_inventory);
      if (quantity > 0) {
        orderLines.push({
          variant_id: variant.id,
          quantity: quantity,
        });
      }
    });

    return sendOrder(storeId, orderLines, shippingAddress, shippingMethodCode, poNumber);
  };

  render() {
    const { variants, errorMessage } = this.props;
    const { errors: quantityErrors, itemsCost, shippingCost, currency } = this.checkMinimumProducts();
    const creditCardErrors = this.checkRequiredCreditCards();
    const noStoreErrors = this.checkHasStore();
    const allErrors = creditCardErrors.concat(quantityErrors, noStoreErrors);

    if (errorMessage) {
      allErrors.push(errorMessage);
    }

    const hasErrors = allErrors.length > 0;

    const columns = [
      {
        text: 'Supplier',
        dataField: 'supplier_name',
        sort: true,
      },
      {
        text: 'Preview',
        dataField: 'square_images',
        formatter: formatImages,
      },
      {
        text: 'Title',
        dataField: 'title',
        sort: true,
      },
      {
        text: 'SKU',
        dataField: 'sku',
        sort: true,
      },
      {
        text: 'Options',
        dataField: 'options',
        formatter: formatOptions,
      },
      {
        text: 'Price',
        dataField: 'wholesale_price',
        sort: true,
        formatter: (cell, row) => currencyFormat(cell, row.currency),
      },
      {
        text: 'Quantity',
        dataField: 'quantity',
        formatter: formatQuantity,
        formatExtraData: {
          handleOnChangeInputSlider: this.handleOnChangeInputSlider,
          handleDelete: this.handleDelete,
          quantities: this.state.quantities,
          saveQuantityToSharedStorage: this.saveQuantityToSharedStorage,
        },
      },
    ];

    const noDataIndication = () => `There are no products in your Cart`;

    return (
      <>
        <div className={styles.cart}>
          <span>Cart</span>
        </div>
        <div className={styles.errors}>
          {allErrors.map((message, i) => (
            <div key={i}>{message}</div>
          ))}
        </div>
        <div className={styles.wrapper}>
          <div className={styles.table}>
            <BootstrapTable
              data={variants}
              columns={columns}
              keyField="sku"
              striped
              bordered={false}
              hover
              className={styles.container}
              noDataIndication={noDataIndication}
              sort={{
                sortCaret: sortCaret,
              }}
              defaultSorted={[
                {
                  dataField: 'sku',
                  order: 'asc',
                },
              ]}
            ></BootstrapTable>

            {variants.length > 0 && (
              <div className={styles.total_block}>
                <div className={styles.inner}>
                  <div>
                    <p className={styles.title}>Sub Total:</p>
                    <p className={styles.count}>{currencyFormat(itemsCost, currency)}</p>
                  </div>
                  <div>
                    <p className={styles.title}>Shipping Cost:</p>
                    <p className={styles.count}>{currencyFormat(shippingCost, currency)}</p>
                  </div>
                  <div>
                    <p className={styles.title}>Total:</p>
                    <p className={styles.count}>{currencyFormat(itemsCost + shippingCost, currency)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <StoreFormContainer hasErrors={hasErrors} sendOrder={this.sendOrder} />
        </div>
      </>
    );
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.changed) {
      return {
        quantities: variantsToMap(nextProps.variants),
      };
    }
    return null;
  }
}

function variantsToMap(variants) {
  return Object.fromEntries(variants.map((variant) => [variant.id, variant]));
}

export default Cart;

Cart.propTypes = {
  sendOrder: PropTypes.func.isRequired,
  setQuantityItem: PropTypes.func.isRequired,
  deleteOrderLine: PropTypes.func.isRequired,
  variants: PropTypes.arrayOf(PropTypes.object).isRequired,
  retailerId: PropTypes.number.isRequired,
  retailerSuppliers: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasStore: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
};
